import React from 'react';
import { Mold } from '../../types/mold';
import { useAppState } from '../../context/AppStateContext';

type StatusConfig = {
  textClass: string;
  bgClass: string;
};

type MoldStatusType = 'active' | 'in_servicing' | 'repairing' | 'eol' | 'closed' | 'inactive' | 'arriving';

type MoldStatusConfig = {
  [key in MoldStatusType]: StatusConfig;
};

const MOLD_STATUS: MoldStatusConfig = {
  active: {
    textClass: 'bg-green-100 text-green-800',
    bgClass: 'bg-green-400',
  },
  in_servicing: {
    textClass: 'bg-amber-100 text-amber-800',
    bgClass: 'bg-amber-400',
  },
  repairing: {
    textClass: 'bg-red-100 text-red-800',
    bgClass: 'bg-red-400',
  },
  eol: {
    textClass: 'bg-gray-100 text-gray-800',
    bgClass: 'bg-gray-400',
  },
  closed: {
    textClass: 'bg-slate-200 text-slate-800',
    bgClass: 'bg-slate-500',
  },
  inactive: {
    textClass: 'bg-red-100 text-red-800',
    bgClass: 'bg-red-400',
  },
  arriving: {
    textClass: 'bg-blue-100 text-blue-800',
    bgClass: 'bg-blue-400',
  }
};

export const getMoldStatus = (status: string): StatusConfig => {
  return status in MOLD_STATUS
    ? MOLD_STATUS[status as MoldStatusType]
    : {
      textClass: 'bg-sky-100 text-sky-800',
      bgClass: 'bg-sky-400',
    };
};

export const getDisplayColorStatus = (status: string): string =>
  getMoldStatus(status).textClass;
export const getColorStatus = (status: string): string =>
  getMoldStatus(status).bgClass;

interface MoldStatusProps {
  mold: Mold;
}
const MoldStatus: React.FC<MoldStatusProps> = ({ mold }) => {
  const { mold: { statusLabelMapping } } = useAppState()
  const status = (
    <span
      data-cy="mold-status"
      className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-sm font-medium ${getDisplayColorStatus(mold.status)}`}
    >
      <span
        className={`w-2 h-2 mr-2 flex-none rounded-full ${getColorStatus(mold.status)}`}
      />
      {statusLabelMapping[mold.status] || mold.status}
    </span>
  );

  // An EOL mold used as an emergency spare has a working status but is still EOL.
  if (!mold.eolAt || mold.status === 'eol') return status;
  return (
    <span className="inline-flex items-center gap-1">
      {status}
      <span
        data-cy="mold-eol-tag"
        className={`inline-flex items-center whitespace-nowrap px-2 py-1 rounded-full text-xs font-semibold ${getDisplayColorStatus('eol')}`}
        title={`${statusLabelMapping.eol || 'EOL'} ตั้งแต่ ${new Date(mold.eolAt).toLocaleDateString('th-TH')}`}
      >
        EOL
      </span>
    </span>
  );
};

export default MoldStatus;
